// ============================================================
// THE COHESION METER (Squad Mode stage 7) - which kill-teams' pools the
// order strip's head shows to this viewer, and how one reads.
//
// The brother you act as: his kill-team's. The GM acting as nobody (or as
// someone who is no brother): every kill-team with a brother on the stage, one
// meter each (one with no Oath yet says so). Anyone else: none. A meter is only for a pool the
// server has answered with (never a missing pool read as 0, gotcha 13).
//
// Up to 15 points it is a row of cells; past that, a bar.
//
// Pure. Zero React dependency.
// ============================================================
import type { Combatant } from '../roster/types';
import { teamOf } from '../roster/team';
import { isBrother, type KillTeams } from './record';
import type { CohesionDoc, Pool } from './pool';

export interface Meter {
  tag: string;
  /** The team's title, from a brother's sheet. */
  label: string;
  /** A brother of it on the stage (its livery is his). */
  brotherId: string;
  pool: Pool;
  start: number;
  oathName: string;
}

interface MeterFacts {
  roster: readonly Combatant[];
  teams: KillTeams;
  cohesion: CohesionDoc | null;
  actorId: string | null;
  isGm: boolean;
}

function meterOf(f: MeterFacts, tag: string): Meter | null {
  const kt = f.teams.killTeams[tag];
  const pool = f.cohesion?.pools[tag];
  const brother = f.roster.find((c) => isBrother(c, tag));
  if (!kt || !pool || !brother) return null;
  const label = teamOf(brother)?.label || tag;
  return { tag, label, brotherId: brother.id, pool, start: kt.start.start, oathName: kt.oath?.name ?? 'No Oath yet' };
}

/** The meters this viewer sees, in the roster's order of teams. */
export function metersFor(f: MeterFacts): Meter[] {
  const actor = f.actorId ? f.roster.find((c) => c.id === f.actorId) : undefined;
  const own = actor && teamOf(actor)?.tag;
  if (actor && own && isBrother(actor, own)) return [meterOf(f, own)].filter((m): m is Meter => m !== null);
  if (!f.isGm) return [];
  return Object.keys(f.teams.killTeams)
    .map((tag) => meterOf(f, tag))
    .filter((m): m is Meter => m !== null);
}

/** The most points drawn as cells; past it, a bar. */
const MAX_CELLS = 15;

/** How the meter draws: `cells` filled of `total`, or a bar at `fraction` (0..1). */
export function meterShape(
  pool: number,
  start: number,
): { kind: 'cells'; filled: number; total: number } | { kind: 'bar'; fraction: number } {
  const total = Math.max(start, pool);
  if (total > MAX_CELLS) return { kind: 'bar', fraction: total ? pool / total : 0 };
  return { kind: 'cells', filled: pool, total };
}
